/**
 * Template Manager Utility Functions
 * Shared helpers for template operations
 */

import type { Template, TestForm } from '../types';

/**
 * Constructs full API URL from relative path or returns absolute URL as-is
 */
export function getApiUrl(path: string): string {
  // If path is already a full URL (http:// or https://), return it as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Handles image loading errors with fallback logic
 */
export function handleImageError(
  event: React.SyntheticEvent<HTMLImageElement>,
  image: { placeholderUrl?: string; originalName: string; previewUrl?: string },
  isModal = false
): void {
  const imgElement = event.target as HTMLImageElement;

  // Try fallback to placeholder URL for legacy images
  if (image.placeholderUrl && imgElement.src !== image.placeholderUrl) {
    imgElement.src = image.placeholderUrl;
    return;
  }

  // If all URLs fail, show error message
  const errorHtml = isModal
    ? `
      <div class="w-full h-64 flex flex-col items-center justify-center bg-red-50 text-red-600 rounded-lg">
        <div class="text-4xl mb-4">⚠️</div>
        <div class="text-center">
          <div class="font-medium">Failed to load preview</div>
          <div class="text-sm mt-1">${image.originalName}</div>
          <div class="text-xs mt-2 text-xtnd-dark-400">URL: ${image.previewUrl}</div>
        </div>
      </div>
    `
    : `
      <div class="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-600">
        <div class="text-2xl mb-2">⚠️</div>
        <div class="text-xs text-center px-2">Failed to load<br/>${image.originalName}</div>
      </div>
    `;

  imgElement.style.display = 'none';
  imgElement.parentElement!.innerHTML = errorHtml;
}

/**
 * Validates form field and shows error toast if invalid
 */
export function validateField(
  value: string | File | null | undefined,
  fieldName: string,
  showToast: any
): boolean {
  if (!value || (typeof value === 'string' && !value.trim())) {
    showToast.error(`Please ${typeof value === 'object' ? 'select' : 'enter'} ${fieldName}`);
    return false;
  }
  return true;
}

/**
 * Validates JSON string
 */
export function validateJson(jsonString: string, showToast: any): any {
  try {
    return JSON.parse(jsonString);
  } catch {
    showToast.error('Invalid JSON in sample data');
    return null;
  }
}

/**
 * Builds FormData for template upload
 */
export function buildTemplateUploadFormData(
  file: File,
  templateName: string,
  templateType: string,
  description: string,
  tags: string
): FormData {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('templateName', templateName);
  formData.append('templateType', templateType);
  formData.append('description', description);
  formData.append('tags', tags);
  return formData;
}

/**
 * Triggers file download
 */
export function downloadFile(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Copies text to clipboard and shows success toast
 */
export function copyToClipboard(text: string, showToast: any, message = 'Copied to clipboard!'): void {
  navigator.clipboard.writeText(text);
  showToast.success(message);
}

/**
 * Gets default upload form state
 */
export function getDefaultUploadForm() {
  return {
    templateName: '',
    templateType: 'pdf' as 'pdf' | 'email' | 'document',
    description: '',
    tags: '',
  };
}

/**
 * Gets file from file input element
 */
export function getFileFromInput(inputId: string): File | null {
  const fileInput = document.getElementById(inputId) as HTMLInputElement;
  return fileInput?.files?.[0] || null;
}

/**
 * Loads template sample data into form
 */
export function loadTemplateSampleData(
  templates: Template[],
  templateId: string,
  setTestForm: (updater: (prev: TestForm) => TestForm) => void,
  showToast: any
): void {
  const selectedTemplate = templates.find(t => t.templateId === templateId);

  if (selectedTemplate?.sampleData) {
    setTestForm(prev => ({
      ...prev,
      sampleData: JSON.stringify(selectedTemplate.sampleData, null, 2),
    }));
    showToast.success('Template sample data loaded!');
  } else {
    showToast.warning('No sample data available for this template.');
  }
}

/**
 * Loads Juniper data from session storage
 */
export function loadJuniperData(
  setTestForm: (updater: (prev: TestForm) => TestForm) => void,
  showToast: any
): void {
  const savedData = sessionStorage.getItem('juniperData');
  if (savedData) {
    try {
      const parsed = JSON.parse(savedData);
      setTestForm(prev => ({
        ...prev,
        sampleData: JSON.stringify(parsed, null, 2),
      }));
      showToast.success('Juniper data loaded successfully!');
    } catch (error) {
      showToast.error('Failed to load Juniper data: Invalid JSON');
    }
  } else {
    showToast.warning('No Juniper data found. Please fetch data from Juniper Settings first.');
  }
}

/**
 * Parses template tags into a normalized array
 */
export function parseTemplateTags(tags: string[] | string | undefined): string[] {
  if (!tags) return [];

  const tagsArray = Array.isArray(tags) ? tags : tags.split(',').map(t => t.trim());

  return tagsArray.filter(tag => tag.length > 0);
}

/**
 * Builds query parameter string for template type filtering
 */
export function buildTemplateTypeParam(templateType: string): string {
  return templateType !== 'all' ? `?type=${templateType}` : '';
}

